import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from './components/AppShell'

const OverviewPage = lazy(() => import('./pages/OverviewPage').then((module) => ({ default: module.OverviewPage })))
const ExplorePage = lazy(() => import('./pages/ExplorePage').then((module) => ({ default: module.ExplorePage })))
const MunicipalityPage = lazy(() => import('./pages/MunicipalityPage').then((module) => ({ default: module.MunicipalityPage })))
const NumericIcmPage = lazy(() => import('./pages/NumericIcmPage').then((module) => ({ default: module.NumericIcmPage })))
const NumericIcmMunicipalityPage = lazy(() => import('./pages/NumericIcmMunicipalityPage').then((module) => ({ default: module.NumericIcmMunicipalityPage })))
const ComparePage = lazy(() => import('./pages/ComparePage').then((module) => ({ default: module.ComparePage })))
const SensitivityPage = lazy(() => import('./pages/SensitivityPage').then((module) => ({ default: module.SensitivityPage })))
const MethodologyPage = lazy(() => import('./pages/MethodologyPage').then((module) => ({ default: module.MethodologyPage })))
const DataPage = lazy(() => import('./pages/DataPage').then((module) => ({ default: module.DataPage })))
const AboutPage = lazy(() => import('./pages/AboutPage').then((module) => ({ default: module.AboutPage })))
const ExecutionsPage = lazy(() => import('./pages/ExecutionsPage').then((module) => ({ default: module.ExecutionsPage })))
const TerritorialListsPage = lazy(() => import('./pages/TerritorialListsPage').then((module) => ({ default: module.TerritorialListsPage })))
const PopulationSegmentationPage = lazy(() => import('./pages/PopulationSegmentationPage').then((module) => ({ default: module.PopulationSegmentationPage })))
const ExperimentsPage = lazy(() => import('./pages/ExperimentsPage').then((module) => ({ default: module.ExperimentsPage })))
const ScenarioLabPage = lazy(() => import('./pages/ScenarioLabPage').then((module) => ({ default: module.ScenarioLabPage })))
const CasaCivilPage = lazy(() => import('./pages/CasaCivilPage').then((module) => ({ default: module.CasaCivilPage })))
const AuditReviewPage = lazy(() => import('./pages/AuditReviewPage').then((module) => ({ default: module.AuditReviewPage })))
const CutoffLabPage = lazy(() => import('./pages/CutoffLabPage').then((module) => ({ default: module.CutoffLabPage })))
const AnalysisGuidePage = lazy(() => import('./pages/AnalysisGuidePage').then((module) => ({ default: module.AnalysisGuidePage })))
const SourceRevisionPage = lazy(() => import('./pages/SourceRevisionPage').then((module) => ({ default: module.SourceRevisionPage })))
const SourceRevisionCasaCivilPage = lazy(() => import('./pages/SourceRevisionCasaCivilPage').then((module) => ({ default: module.SourceRevisionCasaCivilPage })))
const IcmCalibrationPage = lazy(() => import('./pages/IcmCalibrationPage').then((module) => ({ default: module.IcmCalibrationPage })))
const CasaCivilRankAtlasPage = lazy(() => import('./pages/CasaCivilRankAtlasPage').then((module) => ({ default: module.CasaCivilRankAtlasPage })))
const PnaAnaAtlasPage = lazy(() => import('./pages/PnaAnaAtlasPage').then((module) => ({ default: module.PnaAnaAtlasPage })))
const PnaAnaClassesPage = lazy(() => import('./pages/PnaAnaClassesPage').then((module) => ({ default: module.PnaAnaClassesPage })))
const PnaAnaRobustnessPage = lazy(() => import('./pages/PnaAnaRobustnessPage').then((module) => ({ default: module.PnaAnaRobustnessPage })))

function RouteLoading() {
  const { t } = useTranslation()
  return (
    <div className="space-y-4" aria-label={t('common.loadingPage')}>
      <div className="skeleton h-28 w-full" />
      <div className="grid gap-4 md:grid-cols-3">
        <div className="skeleton h-40" />
        <div className="skeleton h-40" />
        <div className="skeleton h-40" />
      </div>
      <div className="skeleton h-[420px]" />
    </div>
  )
}

function LegacyMunicipalityRedirect({ section }: { section?: string }) {
  const { code = '' } = useParams()
  const location = useLocation()
  return (
    <Navigate
      replace
      to={{
        pathname: `/municipios/${code}`,
        search: location.search,
        hash: section ? `#${section}` : '',
      }}
    />
  )
}

export function App() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<OverviewPage />} />
          <Route path="explorar" element={<ExplorePage />} />
          <Route path="municipios/:code" element={<MunicipalityPage />} />
          <Route path="icm-numerico" element={<NumericIcmPage />} />
          <Route path="icm-numerico/municipios/:code" element={<NumericIcmMunicipalityPage />} />
          <Route path="comparar" element={<ComparePage />} />
          <Route path="segmentacao" element={<PopulationSegmentationPage />} />
          <Route path="segmentacao/municipios/:code" element={<LegacyMunicipalityRedirect section="porte" />} />
          <Route path="experimentos" element={<ExperimentsPage />} />
          <Route path="experimentos/:experiment/municipios/:code" element={<LegacyMunicipalityRedirect section="modelos" />} />
          <Route path="laboratorio" element={<ScenarioLabPage />} />
          <Route path="factsheet/:code" element={<LegacyMunicipalityRedirect section="modelos" />} />
          <Route path="casa-civil" element={<CasaCivilPage />} />
          <Route path="casa-civil/municipios/:code" element={<LegacyMunicipalityRedirect section="casa-civil" />} />
          <Route path="casa-civil/revisao" element={<AuditReviewPage />} />
          <Route path="laboratorio-cortes" element={<CutoffLabPage />} />
          <Route path="guia-analises" element={<AnalysisGuidePage />} />
          <Route path="revisao-fontes" element={<SourceRevisionPage />} />
          <Route path="revisao-fontes/casa-civil" element={<SourceRevisionCasaCivilPage />} />
          <Route path="revisao-fontes/casa-civil/ranks" element={<CasaCivilRankAtlasPage />} />
          <Route path="revisao-fontes/calibracao-icm" element={<IcmCalibrationPage />} />
          <Route path="revisao-fontes/municipios/:code" element={<LegacyMunicipalityRedirect section="modelos" />} />
          <Route path="mc-pna-ana" element={<PnaAnaAtlasPage />} />
          <Route path="mc-pna-ana/classes" element={<PnaAnaClassesPage />} />
          <Route path="mc-pna-ana/robustez" element={<PnaAnaRobustnessPage />} />
          <Route path="sensibilidade" element={<SensitivityPage />} />
          <Route path="metodologia" element={<MethodologyPage />} />
          <Route path="dados" element={<DataPage />} />
          <Route path="execucoes" element={<ExecutionsPage />} />
          <Route path="listas" element={<TerritorialListsPage />} />
          <Route path="sobre" element={<AboutPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
